import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WorkerShell } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n";
import { todayLocalISO as todayISO } from "@/lib/dateHelpers";
import { useGeoPermission, requestGeoPermission } from "@/hooks/useGeoPermission";

/**
 * Worker Dashboard v2 — phone-first landing that shows the tech's
 * "Next Stop" as a hero card, followed by today's route in order.
 * Fallback: append `?v=classic` to the URL to render legacy WorkerDashboard.
 */
export default function WorkerDashboardV2() {
  const { t } = useT();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staleNudges, setStaleNudges] = useState([]);
  const geoState = useGeoPermission();

  // Fetches the current worker's route. Called on mount, on a 3-min
  // interval, and every time the tab comes back to the foreground —
  // so schedule changes made by admin appear without a manual refresh.
  // Focus/visibilitychange are DEBOUNCED (30s min gap) so tenant-wide
  // reconnect stampedes during a redeploy can't saturate the backend.
  const refetch = useRef(null);
  const lastFetchAt = useRef(0);
  useEffect(() => {
    let alive = true;
    const run = async (source = "poll") => {
      const now = Date.now();
      if (source !== "poll" && source !== "mount" && (now - lastFetchAt.current) < 30000) {
        return;  // debounce burst refetches
      }
      lastFetchAt.current = now;
      try {
        const [j, n] = await Promise.all([
          api.get("/jobs"),
          api.get("/worker/stale-nudges").catch(() => ({ data: { nudges: [] } })),
        ]);
        if (alive) {
          setJobs(j.data || []);
          setStaleNudges((n.data && n.data.nudges) || []);
        }
      } finally { if (alive) setLoading(false); }
    };
    refetch.current = run;
    run("mount");
    const iv = setInterval(() => run("poll"), 180000);  // 3 min
    const onVis = () => { if (document.visibilityState === "visible") run("visibility"); };
    const onFocus = () => run("focus");
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false; clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // ── Derived buckets ────────────────────────────────────────────────
  const today = todayISO();
  const myId = user?.id;

  // A WO is "mine" if I'm the assignee OR helper OR pinned to my truck on
  // the Sweeper Radar. truck_key for a solo-driver truck is literally the
  // worker's user_id, so pinning to Jhan's truck sets truck_key = Jhan's id.
  const isMine = (j) => (
    j.assigned_to === myId ||
    j.helper_id === myId ||
    (j.sweeper_pin && j.sweeper_pin.truck_key === myId)
  );
  // Sweeper pins carry their own `date` — treat pin.date OR scheduled_for
  // as "today" so pinning to a specific day surfaces the WO on that day's
  // dashboard even when scheduled_for is blank.
  const isScheduledToday = (j) => (
    (j.scheduled_for || "").slice(0, 10) === today ||
    (j.sweeper_pin && j.sweeper_pin.date === today)
  );
  const notDone = (j) => !["completed", "canceled"].includes((j.status || "").toLowerCase());

  const onClock = useMemo(() =>
    jobs.find(j => isMine(j) && j.clock_in_at && !j.clock_out_at && notDone(j))
  , [jobs, myId]); // eslint-disable-line

  const todayList = useMemo(() => {
    // My jobs scheduled today, sorted by sweeper route_order if present, else priority
    const mine = jobs.filter(j => isMine(j) && isScheduledToday(j) && notDone(j));
    const pri = { urgent: 0, emergency: 0, high: 1, normal: 2, low: 3 };
    return mine.sort((a, b) => {
      const ao = a.sweeper_pin?.route_order;
      const bo = b.sweeper_pin?.route_order;
      if (ao != null && bo != null) return ao - bo;
      if (ao != null) return -1;
      if (bo != null) return 1;
      return (pri[(a.priority || "").toLowerCase()] ?? 4) - (pri[(b.priority || "").toLowerCase()] ?? 4);
    });
  }, [jobs, myId, today]); // eslint-disable-line

  const upcoming = useMemo(() => {
    const mine = jobs.filter(j => isMine(j) && !isScheduledToday(j) && notDone(j) && (j.scheduled_for || "") > today);
    return mine.sort((a, b) => (a.scheduled_for || "").localeCompare(b.scheduled_for || "")).slice(0, 8);
  }, [jobs, myId, today]); // eslint-disable-line

  const doneToday = useMemo(() =>
    jobs.filter(j => isMine(j) && isScheduledToday(j) && j.status === "completed").length
  , [jobs, myId, today]); // eslint-disable-line

  const poolCount = useMemo(() =>
    jobs.filter(j => !j.assigned_to && notDone(j)).length
  , [jobs]);

  // "Next stop" = first todayList entry that isn't the on-clock one
  const nextStop = useMemo(() => {
    return todayList.find(j => !j.clock_in_at || j.clock_out_at);
  }, [todayList]);

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const timerMins = onClock ? Math.max(0, Math.floor((Date.now() - new Date(onClock.clock_in_at).getTime()) / 60000)) : 0;

  return (
    <WorkerShell>
      <div className="pb-24" data-testid="wd-v2">
        {/* Greeting */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-[#0B1F3A] leading-tight" data-testid="wd-greeting">
              {greetingFor(user?.name)}
            </div>
            <div className="text-sm text-slate-500 mt-0.5">
              {todayList.length > 0
                ? `${todayList.length} stop${todayList.length === 1 ? "" : "s"} today · ${doneToday} done`
                : "No stops today — pool has jobs waiting"}
            </div>
          </div>
          <Link to="/worker?v=classic" data-testid="wd-open-classic"
            className="text-[10px] font-bold text-slate-400 hover:text-[#0B1F3A] underline">
            Classic
          </Link>
        </div>

        {/* ── GPS banner ─────────────────────────────────────────
            Soft warning that persists at the top of the dashboard when
            location services are off. Workers can't clock in without GPS
            (backend enforces this), so we show a red pill + a one-tap
            Enable button that triggers the browser prompt. */}
        {(geoState === "denied" || geoState === "prompt" || geoState === "unsupported") && (
          <div className="mx-4 mt-2 rounded-lg border-2 border-red-500 bg-red-50 px-3 py-2.5 flex items-center gap-3"
               data-testid="wd-gps-banner">
            <div className="h-8 w-8 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0 text-lg">
              📍
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-black uppercase tracking-widest text-red-700">
                {geoState === "denied" ? "Location Blocked"
                  : geoState === "unsupported" ? "Location Not Supported"
                  : "Turn on Location"}
              </div>
              <div className="text-xs text-slate-800 leading-tight">
                {geoState === "denied"
                  ? "You blocked GPS earlier. Tap the 🔒 lock in your address bar → Allow Location → refresh."
                  : geoState === "unsupported"
                  ? "This browser can't share GPS. Open Field Radar in Chrome or Safari to clock in."
                  : "You can't clock in or start a WO until location is enabled."}
              </div>
            </div>
            {geoState === "prompt" && (
              <button
                onClick={async () => {
                  const next = await requestGeoPermission();
                  if (next === "granted") {
                    // No native way to force useGeoPermission to re-run —
                    // simplest is a soft reload once the browser prompt
                    // has been answered. Fast on mobile.
                    window.location.reload();
                  }
                }}
                data-testid="wd-gps-enable-btn"
                className="text-[10px] font-black uppercase tracking-widest text-white bg-red-600 hover:bg-red-700 rounded-md px-2.5 py-1.5">
                Enable
              </button>
            )}
          </div>
        )}

        {/* ── Stale-WO nudges ────────────────────────────────────
            Backend cron pushes here every 15 min for any assigned WO
            still un-started 2h+ past its scheduled time. Same data
            arrives as a Web Push so the phone taps light up too. */}
        {staleNudges.length > 0 && (
          <div className="mx-4 mt-2 space-y-2" data-testid="wd-stale-nudges">
            {staleNudges.slice(0, 3).map(n => (
              <Link
                key={n.job_id}
                to={`/worker/jobs/${n.job_id}`}
                data-testid={`wd-stale-nudge-${n.job_id}`}
                className="block rounded-lg border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="text-lg">⏰</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">
                      Still not started · {n.hours_stale ? `${n.hours_stale}h late` : "past due"}
                    </div>
                    <div className="text-xs font-bold text-slate-900 truncate">
                      WO {n.wo_number} · {n.address}
                    </div>
                  </div>
                  <div className="text-xs font-black text-amber-700">Open →</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* On the clock (highest priority) */}
        {onClock && (
          <div className="mx-4 mt-3 rounded-2xl overflow-hidden shadow-sm" data-testid="wd-on-clock-card">
            <div className="px-5 py-4 relative" style={{ background: "linear-gradient(135deg, #0B1F3A 0%, #00C4CC 100%)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1.5 bg-white/95 text-emerald-600 px-2.5 py-1 rounded-full text-[10px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"/>
                  ON THE CLOCK
                </span>
                <span className="bg-[#00C4CC] text-white px-2.5 py-1 rounded-full text-[10px] font-bold">
                  ⏱ {Math.floor(timerMins / 60)}h {timerMins % 60}m
                </span>
              </div>
              <div className="text-white text-lg font-bold leading-tight" data-testid="wd-on-clock-address">
                {onClock.property_address}{onClock.unit ? ` · Unit ${onClock.unit}` : ""}
              </div>
              <div className="text-white/85 text-xs mt-1">
                {onClock.title || onClock.description}
              </div>
              <div className="text-white/70 text-[11px] mt-2">Started {fmtTime(onClock.clock_in_at)}</div>
            </div>
            <Link to={`/worker/jobs/${onClock.id}`} data-testid="wd-open-on-clock"
              className="block px-5 py-3 bg-white text-[#00C4CC] font-bold text-sm hover:bg-slate-50 text-center">
              Back to work →
            </Link>
          </div>
        )}

        {/* Next Stop — big card */}
        {!onClock && nextStop && (
          <div className="mx-4 mt-3 rounded-2xl overflow-hidden shadow-sm bg-white" data-testid="wd-next-stop-card">
            <div className="px-5 py-4 relative" style={{ background: "linear-gradient(135deg, #0B1F3A 0%, #00C4CC 100%)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/85 text-[10px] font-bold uppercase tracking-widest">Next stop</span>
                {nextStop.sweeper_pin?.route_order != null && (
                  <span className="bg-white/95 text-[#0B1F3A] px-2 py-0.5 rounded-full text-[10px] font-bold">
                    STOP {nextStop.sweeper_pin.route_order + 1}
                  </span>
                )}
              </div>
              <div className="text-white text-xl font-bold tracking-tight leading-tight" data-testid="wd-next-stop-address">
                {nextStop.property_address}{nextStop.unit ? ` · Unit ${nextStop.unit}` : ""}
              </div>
              <div className="text-white/85 text-sm mt-1.5">
                {nextStop.title || nextStop.description}
              </div>
              <div className="flex gap-1.5 mt-2.5">
                {(nextStop.priority || "").toLowerCase() === "urgent" && <Badge tint="red">🚨 Emergency</Badge>}
                {nextStop.is_turn && <Badge tint="amber">Turn</Badge>}
                {nextStop.return_visit?.date && <Badge tint="blue">Return visit</Badge>}
                {nextStop.habitability && <Badge tint="red">Habitability</Badge>}
              </div>
            </div>
            <div className="flex bg-white">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(nextStop.property_address || "")}&travelmode=driving`}
                target="_blank" rel="noreferrer"
                data-testid="wd-next-stop-directions"
                className="flex-1 py-3.5 text-center text-sm font-bold text-[#0B1F3A] hover:bg-slate-50 border-r border-slate-100">
                🗺 Directions
              </a>
              <Link to={`/worker/jobs/${nextStop.id}`} data-testid="wd-open-next-stop"
                className="flex-1 py-3.5 text-center text-sm font-bold text-[#00C4CC] hover:bg-slate-50">
                Open job →
              </Link>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!onClock && !nextStop && !loading && (
          <div className="mx-4 mt-4 rounded-2xl bg-white shadow-sm p-8 text-center" data-testid="wd-empty">
            <div className="text-4xl mb-2">☕</div>
            <div className="font-bold text-[#0B1F3A] text-lg">Nothing scheduled today</div>
            <div className="text-slate-500 text-sm mt-1 mb-4">
              {poolCount > 0
                ? `${poolCount} job${poolCount === 1 ? "" : "s"} in the pool — claim one to start.`
                : "You're all caught up. Nice work."}
            </div>
            {poolCount > 0 && (
              <Link to="/worker?v=classic" data-testid="wd-open-pool"
                className="inline-block h-11 px-6 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] text-white font-bold text-sm leading-[44px]">
                Open pool ({poolCount})
              </Link>
            )}
          </div>
        )}

        {/* Today's route list */}
        {todayList.length > 1 && (
          <div className="mx-4 mt-5" data-testid="wd-today-list">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 px-1">
              Today's route
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {todayList.map((j, i) => {
                const isCurrent = onClock?.id === j.id;
                const isDone = j.status === "completed";
                return (
                  <Link key={j.id} to={`/worker/jobs/${j.id}`} data-testid={`wd-stop-${j.id}`}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50
                      ${isCurrent ? "bg-[#E6FAFB]" : ""}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                      ${isDone ? "bg-emerald-100 text-emerald-700"
                        : isCurrent ? "bg-[#00C4CC] text-white"
                        : "bg-slate-100 text-slate-600"}`}>
                      {isDone ? "✓" : (j.sweeper_pin?.route_order != null ? j.sweeper_pin.route_order + 1 : i + 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold truncate ${isDone ? "text-slate-400 line-through" : "text-[#0B1F3A]"}`}>
                        {j.property_address}{j.unit ? ` · Unit ${j.unit}` : ""}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{j.title}</div>
                    </div>
                    {(j.priority || "").toLowerCase() === "urgent" && !isDone && (
                      <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded flex-shrink-0">EM</span>
                    )}
                    {isCurrent && !isDone && (
                      <span className="text-[10px] font-bold uppercase bg-[#00C4CC] text-white px-1.5 py-0.5 rounded flex-shrink-0">NOW</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming */}
        {upcoming.length > 0 && (
          <div className="mx-4 mt-5" data-testid="wd-upcoming-list">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 px-1">
              Upcoming ({upcoming.length})
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {upcoming.map(j => (
                <Link key={j.id} to={`/worker/jobs/${j.id}`} data-testid={`wd-upcoming-${j.id}`}
                  className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <div className="w-11 flex-shrink-0 text-center">
                    <div className="text-[9px] font-bold uppercase text-slate-400 leading-tight">
                      {new Date(j.scheduled_for + "T12:00").toLocaleDateString([], { weekday: "short" })}
                    </div>
                    <div className="text-lg font-bold text-[#0B1F3A] leading-none">
                      {new Date(j.scheduled_for + "T12:00").getDate()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0B1F3A] truncate">
                      {j.property_address}{j.unit ? ` · Unit ${j.unit}` : ""}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{j.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer utility strip */}
        <div className="mx-4 mt-5 flex gap-2">
          <Link to="/worker?v=classic" data-testid="wd-view-all"
            className="flex-1 h-11 rounded-lg bg-white text-[#0B1F3A] text-sm font-bold border border-slate-200 inline-flex items-center justify-center hover:bg-slate-50">
            View all jobs
          </Link>
          <Link to="/worker/team-chat" data-testid="wd-team-chat"
            className="flex-1 h-11 rounded-lg bg-white text-[#0B1F3A] text-sm font-bold border border-slate-200 inline-flex items-center justify-center hover:bg-slate-50">
            💬 Team chat
          </Link>
        </div>

        {loading && (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="wd-loading">Loading your day…</div>
        )}
      </div>
    </WorkerShell>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────
function greetingFor(name) {
  const h = new Date().getHours();
  const g = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const first = (name || "").trim().split(" ")[0] || "";
  return first ? `${g}, ${first}` : g;
}

function Badge({ tint, children }) {
  const tints = {
    red:   "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-800",
    blue:  "bg-blue-100 text-blue-800",
  };
  return (
    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${tints[tint] || tints.blue} bg-opacity-90`}>
      {children}
    </span>
  );
}
